import {
  createAuthStore,
  store,
  type AuthSession,
  type AuthState,
} from '@updater/auth';

/**
 * Auth store for the guest flows, isolated from the default store that holds
 * the signed-in (moving app) session.
 *
 * Guest routes authenticate with an anonymous `createPublicSession` token. That
 * token must never reach the signed-in code path: `persistAuth` would write it
 * to `localStorage.session` and to a `.updater.com` cookie, then rehydrate it as
 * `mode: 'authenticated'` on the next load — which would make
 * `useIsAuthenticated()` true for a guest and let a 401 on a guest request sign
 * the user out of their real session in other tabs.
 *
 * Keeping the guest session in its own store makes that impossible by
 * construction rather than by remembering to strip it at each consumer.
 */
export const guestAuthStore = createAuthStore();

export function getGuestAuthState(): AuthState {
  return guestAuthStore.get(store.sessionAtom);
}

/** The headers to authenticate a guest request, or undefined if there is no session yet. */
export function getGuestSession(): AuthSession | undefined {
  return getGuestAuthState().session;
}

export function setGuestAuthState(state: AuthState): void {
  store.setSession(state, guestAuthStore);
}

export function subscribeGuestAuth(
  subscriber: (state: AuthState) => void
): () => void {
  return store.subscribe(subscriber, guestAuthStore);
}
